import { createAction, props } from "@ngrx/store";
import { CustomersSortField, ProgramSections, RewardsView } from "./state";
import { WalletConfigUpdate } from "../../interfaces/wallet-config.interface";
import type { McpSettings } from "../../interfaces";
import type { OnboardingRewardNameKey } from "../../services/i18n/customer-copies/onboarding-customer-copy";
import type { SubscriptionPaymentRequiredReason } from "../../modals/subscription/subscription.interface";
import type { ProfileFormSaveError } from "../../utils/profile-form/profile-form";
import {
  CustomerMetafieldDefinitionDto,
  CreateCustomerMetafieldDefinitionDto,
  AutoTranslateDto,
  EntityTranslation,
  ProgramLanguagesResponseDto,
  TemplateMetadata,
  SetProgramLanguagesDto,
  UpsertTranslationsBulkDto,
  AdminControllerGetProgramCampaignsV1RequestParams,
  AdminControllerGetProgramLocationsV1RequestParams,
  AnalyticsControllerGetAnalyticsV1RequestParams,
  CampaignDto,
  EmailConfig,
  Link,
  Program,
  ProgramAnalytics,
  WebConfig,
  WidgetConfig,
  Location,
  AdminControllerAddLocationV1RequestParams,
  AdminControllerUpdateLocationV1RequestParams,
  AdminControllerDeleteLocationV1RequestParams,
  AdminControllerUpdateCampaignV1RequestParams,
  AdminControllerDeleteCampaignV1RequestParams,
  AdminControllerCreateCampaignV1RequestParams,
  Boost,
  AdminControllerGetBoostsV1RequestParams,
  AdminControllerCreateBoostV1RequestParams,
  AdminControllerUpdateBoostV1RequestParams,
  AdminControllerDeleteBoostV1RequestParams,
  AdminControllerGetProgramApiKeysV1RequestParams,
  ApiKey,
  AdminControllerDeleteApiKeyV1RequestParams,
  AdminControllerGenerateApiKeyV1RequestParams,
  AdminControllerGetProgramApiKeyV1RequestParams,
  AdminControllerUpdateApiKeyV1RequestParams,
  ProgramControllerGetProgramIntegrationsV1RequestParams,
  ProgramIntegration,
  AdminControllerAddIntegrationV1RequestParams,
  AdminControllerGetProgramCustomersV1RequestParams,
  PaginatedCustomers,
  AdminControllerGetCustomerByIdV1RequestParams,
  CustomerData,
  CustomerActionsControllerSendPushV1RequestParams,
  ShopifyControllerTokenExchangeV1RequestParams,
  AnalyticsControllerGetAnalyticsAggregateV1RequestParams,
  ProgramAnalyticsAggregate,
  AdminControllerUpdateProgramIntegrationV1RequestParams,
  AdminControllerUpdateProgramV1RequestParams,
  KlaviyoControllerAuthorizeV1RequestParams,
  KlaviyoControllerCallbackV1RequestParams,
  Feature,
  Integration,
  SegmentDto,
  AdminControllerGetSegmentsV1RequestParams,
  AdminControllerAddPointsToCustomerV1RequestParams,
  AdminControllerRemovePointsToCustomerV1RequestParams,
  AdminControllerGetCustomerEventsV1RequestParams,
  PaginatedCustomerEvents,
  AdminControllerGetProgramSubscriptionInfoV1RequestParams,
  SubscriptionInfo,
  AdminControllerCreateProgramSubscriptionV1RequestParams,
  AdminControllerCancelProgramSubscriptionV1RequestParams,
  RedirectLink,
  AdminControllerGetCampaignByIdV1RequestParams,
  SlugDto,
  AdminControllerCheckCampaignSlugV1RequestParams,
  CouponControllerGetCouponsV1RequestParams,
  PaginatedCoupons,
  CouponControllerGetCouponByIdV1RequestParams,
  CouponDto,
  CouponControllerCreateCouponBatchV1RequestParams,
  CouponBatchDto,
  CreateCouponBatchDto,
  CouponControllerRemoveCouponBatchV1RequestParams,
  CouponControllerRemoveUnclaimedCodesV1RequestParams,
  CouponControllerSearchCouponsV1RequestParams,
  MinimalCoupon,
  AdminControllerReactivateCampaignV1RequestParams,
  KlaviyoControllerRevokeV1RequestParams,
  CouponControllerGetCouponCountsV1RequestParams,
  CouponCounts,
  CouponControllerClaimAndDownloadCodesV1RequestParams,
  SectionDto,
  CouponControllerGetRewardsV1RequestParams,
  RewardDto,
  RewardWithCouponCodeCountsDto,
  PublicRewardDto,
  CouponControllerGetRewardV1RequestParams,
  CouponControllerCreateRewardV1RequestParams,
  CouponControllerUpdateRewardV1RequestParams,
  CouponControllerDeleteRewardV1RequestParams,
  AdminControllerGetProgramDevicesV1RequestParams,
  Device,
  AdminControllerAddDeviceV1RequestParams,
  AdminControllerLinkPosToDeviceV1RequestParams,
  AdminControllerUnlinkPosFromDeviceV1RequestParams,
  AdminControllerDeleteDeviceV1RequestParams,
  AllowedDomain,
  RegisterField,
  EngagementFlowWithProgramEngagementFlowDto,
  AdminControllerDeleteProgramEngagementFlowV1RequestParams,
  AdminControllerUpdateProgramEngagementFlowV1RequestParams,
  ProgramEngagementFlow,
  AdminControllerCreateProgramEngagementFlowV1RequestParams,
  CouponInfoDto,
  AdminControllerGetProgramTierNamesV1RequestParams,
  Tier,
  AdminControllerGetProgramTiersV1RequestParams,
  AdminControllerPatchProgramTiersV1RequestParams,
  AdminControllerUpdateProgramTierV1RequestParams,
  UpsertTranslationDto,
} from "../../services/api";
import { HttpErrorResponse } from "@angular/common/http";

export const getProgram = createAction(
  "@program/get-program",
  props<
  { programId: string; slug?: string; loadingFullScreen?: boolean } | { programId?: string; slug: string; loadingFullScreen?: boolean }
  >(),
);
export const getProgramSuccess = createAction("@program/get-program-success", props<{ program: Program; loadingFullScreen: boolean }>());
export const getProgramFailure = createAction(
  "@program/get-program-failure",
  props<{ error: HttpErrorResponse; loadingFullScreen: boolean }>(),
);

export const updateProgram = createAction(
  "@program/update-program",
  props<{ params: AdminControllerUpdateProgramV1RequestParams; toastId?: string; callback?: () => void }>(),
);
export const updateProgramSuccess = createAction(
  "@program/update-program-success",
  props<{ program: Program; toastId?: string; callback?: () => void }>(),
);
export const updateProgramFailure = createAction("@program/update-program-failure", props<{ reason: string }>());

export const getProgramLinks = createAction("@program/get-program-links", props<{ programId: string }>());
export const getProgramLinksSuccess = createAction("@program/get-program-links-success", props<{ programLinks: Link[] }>());
export const getProgramLinksFailure = createAction("@program/get-program-links-failure", props<{ reason: string }>());

export const getProgramSections = createAction("@program/get-program-sections", props<{ programId: string }>());
export const getProgramSectionsSuccess = createAction("@program/get-program-sections-success", props<{ programSections: SectionDto[] }>());
export const getProgramSectionsFailure = createAction("@program/get-program-sections-failure", props<{ reason: string }>());

export const getProgramTiers = createAction("@program/get-program-tiers", props<AdminControllerGetProgramTiersV1RequestParams>());
export const getProgramTiersSuccess = createAction("@program/get-program-tiers-success", props<{ tiers: Tier[] }>());
export const getProgramTiersFailure = createAction("@program/get-program-tiers-failure", props<{ reason: string }>());

export const updateProgramTiers = createAction("@program/update-program-tiers", props<AdminControllerPatchProgramTiersV1RequestParams & { loyaltyIntegration: ProgramIntegration; callback: () => void }>());
export const updateProgramTiersSuccess = createAction("@program/update-program-tiers-success", props<{ loyaltyIntegration: ProgramIntegration; tiers: Tier[]; callback: () => void }>());
export const updateProgramTiersFailure = createAction("@program/update-program-tiers-failure", props<{ reason: string }>());

export const updateProgramTier = createAction("@program/update-program-tier", props<AdminControllerUpdateProgramTierV1RequestParams & { callback?: () => void }>());
export const updateProgramTierSuccess = createAction("@program/update-program-tier-success", props<{ tier: Tier; callback?: () => void }>());
export const updateProgramTierFailure = createAction("@program/update-program-tier-failure", props<{ reason: string }>());

export const disableProgramTiers = createAction("@program/disable-program-tiers", props<{ loyaltyIntegration: ProgramIntegration; callback: () => void }>());
export const disableProgramTiersSuccess = createAction("@program/disable-program-tiers-success", props<{ loyaltyIntegration: ProgramIntegration; callback: () => void }>());
export const disableProgramTiersFailure = createAction("@program/disable-program-tiers-failure", props<{ reason: string }>());

export const getProgramTierNames = createAction("@program/get-program-tier-names", props<AdminControllerGetProgramTierNamesV1RequestParams>());
export const getProgramTierNamesSuccess = createAction("@program/get-program-tier-names-success", props<{ tierNames: string[] }>());
export const getProgramTierNamesFailure = createAction("@program/get-program-tier-names-failure", props<{ reason: string }>());

export const updateWallet = createAction("@program/update-wallet", props<{ wallet: WalletConfigUpdate; tier: string }>());
export const addTemporalProgramSection = createAction("@program/add-temporal-program-section", props<{ tier?: string }>());
export const updateTemporalProgramSection = createAction(
  "@program/update-temporal-program-section",
  props<{ value: string; property: keyof SectionDto; id: string; tier?: string }>(),
);
export const deleteTemporalProgramSection = createAction(
  "@program/delete-temporal-program-section",
  props<{ index: number; tier?: string }>(),
);
export const reorderTemporalProgramSections = createAction(
  "@program/reorder-temporal-program-sections",
  props<{ previousIndex: number; currentIndex: number; tier?: string }>(),
);

export const getCampaignLinks = createAction("@program/get-campaign-links", props<{ programId: string }>());
export const getCampaignLinksSuccess = createAction("@program/get-campaign-links-success", props<{ campaignLinks: Link[] }>());
export const getCampaignLinksFailure = createAction("@program/get-campaign-links-failure");
export const updateCampaignLinkSuccess = createAction("@program/update-campaign-link-success", props<{ campaignLink: Link }>());

export const updateEmail = createAction("@program/update-email", props<{ email: EmailConfig }>());

export const updateWebapp = createAction("@program/update-webapp", props<{ web: WebConfig }>());

// null drops the widget draft, leaving the program on its Shopify theme settings
export const updateWidget = createAction("@program/update-widget", props<{ widget: WidgetConfig | null }>());

export const persistProgram = createAction("@program/persist-program", props<{ callback?: () => void }>());
export const persistProgramSuccess = createAction("@program/persist-program-success", props<{ program: Program; callback?: () => void }>());
export const persistProgramFailure = createAction("@program/persist-program-failure", props<{ reason: string }>());

export const persistProgramSectionsSuccess = createAction(
  "@program/persist-program-sections-success",
  props<{ programSections: SectionDto[] }>(),
);

// Puts back the program (and sections) a design editor last saved, dropping the draft it wrote for its preview
export const restoreProgramDraft = createAction(
  "@program/restore-program-draft",
  props<{ program: Program; programSections?: ProgramSections }>(),
);

export const getIntegrations = createAction("@program/get-integrations");
export const getIntegrationsSuccess = createAction("@program/get-integrations-success", props<{ integrations: Integration[] }>());
export const getIntegrationsFailure = createAction("@program/get-integrations-failure");

/** The program GET at start-up returned nothing: drop any program rehydrated from a previous install. */
export const programNotFound = createAction("@program/program-not-found");

export const createProgram = createAction(
  "@program/create-program",
  props<{ name: string; contactEmail: string; callback?: () => void }>(),
);
export const createProgramSuccess = createAction(
  "@program/create-program-success",
  props<{ program: Program; callback?: () => void }>(),
);
export const createProgramFailure = createAction("@program/create-program-failure", props<{ error: string }>());

export const getAnalytics = createAction(
  "@program/get-analytics",
  props<{ params: AnalyticsControllerGetAnalyticsV1RequestParams; loading?: boolean }>(),
);
export const getAnalyticsSuccess = createAction("@program/get-analytics-success", props<{ analytics: ProgramAnalytics }>());
export const getAnalyticsFailure = createAction("@program/get-analytics-failure");

export const getAnalyticsAggregate = createAction(
  "@program/get-analytics-aggregate",
  props<{ params: AnalyticsControllerGetAnalyticsAggregateV1RequestParams; loading?: boolean }>(),
);
export const getAnalyticsAggregateSuccess = createAction(
  "@program/get-analytics-aggregate-success",
  props<{ analyticsAggregateType: string; analyticsAggregate: ProgramAnalyticsAggregate }>(),
);
export const getAnalyticsAggregateFailure = createAction("@program/get-analytics-aggregate-failure");

export const getProgramCustomers = createAction(
  "@program/get-program-customers",
  props<AdminControllerGetProgramCustomersV1RequestParams>(),
);
export const getProgramCustomersSuccess = createAction(
  "@program/get-program-customers-success",
  props<{ limit: number; customers: PaginatedCustomers }>(),
);
export const getProgramCustomersFailure = createAction("@program/get-program-customers-failure");

export const getCustomerById = createAction("@program/get-customer-by-id", props<AdminControllerGetCustomerByIdV1RequestParams>());
export const getCustomerByIdSuccess = createAction("@program/get-customer-by-id-success", props<{ customer: CustomerData }>());
export const getCustomerByIdFailure = createAction("@program/get-customer-by-id-failure");

export const getProgramCampaigns = createAction(
  "@program/get-program-campaigns",
  props<{ params: AdminControllerGetProgramCampaignsV1RequestParams }>(),
);
export const getProgramCampaignsSuccess = createAction("@program/get-program-campaigns-success", props<{ campaigns: CampaignDto[] }>());
export const getProgramCampaignsFailure = createAction("@program/get-program-campaigns-failure");

export const getProgramLocations = createAction(
  "@program/get-program-locations",
  props<AdminControllerGetProgramLocationsV1RequestParams>(),
);
export const getProgramLocationsSuccess = createAction("@program/get-program-locations-success", props<{ locations: Location[] }>());
export const getProgramLocationsFailure = createAction("@program/get-program-locations-failure");

export const addLocation = createAction(
  "@program/add-location",
  props<AdminControllerAddLocationV1RequestParams & { callback: () => void }>(),
);
export const addLocationSuccess = createAction("@program/add-location-success", props<{ location: Location; callback: () => void }>());
export const addLocationFailure = createAction("@program/add-location-failure", props<{ formError: string }>());

export const updateLocation = createAction("@program/update-location", props<AdminControllerUpdateLocationV1RequestParams & { callback?: () => void }>());
export const updateLocationSuccess = createAction("@program/update-location-success", props<{ location: Location; callback?: () => void }>());
export const updateLocationFailure = createAction("@program/update-location-failure", props<{ formError: string }>());

export const deleteLocation = createAction("@program/delete-location", props<AdminControllerDeleteLocationV1RequestParams>());
export const deleteLocationSuccess = createAction("@program/delete-location-success", props<{ locationId: string }>());
export const deleteLocationFailure = createAction("@program/delete-location-failure", props<{ formError: string }>());

export const getProgramDevices = createAction(
  "@program/get-program-devices",
  props<{ params: AdminControllerGetProgramDevicesV1RequestParams; isLongPolling?: boolean }>(),
);
export const getProgramDevicesSuccess = createAction(
  "@program/get-program-devices-success",
  props<{ devices: Device[] }>(),
);
export const getProgramDevicesFailure = createAction("@program/get-program-devices-failure", props<{ error: string }>());

export const addDevice = createAction(
  "@program/add-device",
  props<AdminControllerAddDeviceV1RequestParams & { callback: () => void; force?: boolean }>(),
);
export const addDeviceSuccess = createAction(
  "@program/add-device-success",
  props<{ device: Device; callback: () => void; force?: boolean }>(),
);
export const addDeviceFailure = createAction("@program/add-device-failure", props<{ error: string }>());
export const addDeviceConflict = createAction(
  "@program/add-device-conflict",
  props<AdminControllerAddDeviceV1RequestParams & { callback: () => void }>(),
);

export const linkPosToDevice = createAction(
  "@program/link-pos-to-device",
  props<AdminControllerLinkPosToDeviceV1RequestParams & { callback: () => void }>(),
);
export const linkPosToDeviceSuccess = createAction(
  "@program/link-pos-to-device-success",
  props<{ device: Device; callback: () => void }>(),
);
export const linkPosToDeviceFailure = createAction("@program/link-pos-to-device-failure", props<{ error: string }>());

export const unlinkPosToDevice = createAction("@program/unlink-pos-to-device", props<AdminControllerUnlinkPosFromDeviceV1RequestParams>());
export const unlinkPosToDeviceSuccess = createAction("@program/unlink-pos-to-device-success", props<{ device: Device }>());
export const unlinkPosToDeviceFailure = createAction("@program/unlink-pos-to-device-failure", props<{ error: string }>());

export const deleteDevice = createAction("@program/delete-device", props<AdminControllerDeleteDeviceV1RequestParams>());
export const deleteDeviceSuccess = createAction("@program/delete-device-success", props<{ deviceId: string }>());
export const deleteDeviceFailure = createAction("@program/delete-device-failure", props<{ error: string }>());

export const createCampaign = createAction(
  "@program/create-campaign",
  props<{
    params: AdminControllerCreateCampaignV1RequestParams;
    links?: Link[];
    // Coupon discount configured before the coupon existed (pending mode). When
    // present, the effect persists it against the freshly created coupon.
    pendingCouponBatch?: CreateCouponBatchDto;
    callback?: () => void;
  }>(),
);
export const createCampaignSuccess = createAction(
  "@program/create-campaign-success",
  props<{ campaign: CampaignDto; callback?: () => void }>(),
);
export const createCampaignFailure = createAction("@program/create-campaign-failure", props<{ formError: string }>());

export const updateCampaign = createAction(
  "@program/update-campaign",
  props<{
    params: AdminControllerUpdateCampaignV1RequestParams;
    links?: Link[];
    suppressToast?: boolean;
    correlationId?: string;
    callback?: () => void;
  }>(),
);
export const updateCampaignSuccess = createAction(
  "@program/update-campaign-success",
  props<{ campaign: CampaignDto; suppressToast?: boolean; correlationId?: string; callback?: () => void }>(),
);
export const updateCampaignFailure = createAction(
  "@program/update-campaign-failure",
  props<{ formError: string; correlationId?: string }>(),
);

export const reactivateCampaign = createAction(
  "@program/reactivate-campaign",
  props<AdminControllerReactivateCampaignV1RequestParams & { callback?: () => void }>(),
);

export const deleteCampaign = createAction(
  "@program/delete-campaign",
  props<{ params: AdminControllerDeleteCampaignV1RequestParams; callback?: () => void }>(),
);
export const deleteCampaignSuccess = createAction(
  "@program/delete-campaign-success",
  props<{ campaignId: string; callback?: () => void }>(),
);
export const deleteCampaignFailure = createAction("@program/delete-campaign-failure", props<{ formError: string }>());

// quietFailure: the page shows the failure itself (the boost editor's terminal state), so no page banner
export const getBoosts = createAction(
  "@program/get-boosts",
  props<{ params: AdminControllerGetBoostsV1RequestParams; quietFailure?: boolean }>(),
);
export const getBoostsSuccess = createAction("@program/get-boosts-success", props<{ boosts: Boost[] }>());
export const getBoostsFailure = createAction("@program/get-boosts-failure", props<{ error: string; quietFailure?: boolean }>());

export const createBoost = createAction(
  "@program/create-boost",
  props<{ params: AdminControllerCreateBoostV1RequestParams; callback?: () => void }>(),
);
export const createBoostSuccess = createAction("@program/create-boost-success", props<{ boost: Boost; callback?: () => void }>());
export const createBoostFailure = createAction(
  "@program/create-boost-failure",
  props<{ formError: string; conflictingBoostId?: string }>(),
);

export const updateBoost = createAction(
  "@program/update-boost",
  props<{ params: AdminControllerUpdateBoostV1RequestParams; callback?: () => void }>(),
);
export const updateBoostSuccess = createAction("@program/update-boost-success", props<{ boost: Boost; callback?: () => void }>());
export const updateBoostFailure = createAction(
  "@program/update-boost-failure",
  props<{ formError: string; conflictingBoostId?: string }>(),
);

export const archiveBoost = createAction(
  "@program/archive-boost",
  props<{ params: AdminControllerDeleteBoostV1RequestParams; callback?: () => void }>(),
);
export const archiveBoostSuccess = createAction(
  "@program/archive-boost-success",
  props<{ boostId: string; deletedAt: string; callback?: () => void }>(),
);
export const archiveBoostFailure = createAction("@program/archive-boost-failure", props<{ formError: string }>());

export const resetFormError = createAction("@program/reset-form-error");

export const getProgramApiKeys = createAction("@program/get-program-api-keys", props<AdminControllerGetProgramApiKeysV1RequestParams>());
export const getProgramApiKeysSuccess = createAction("@program/get-program-api-keys-success", props<{ apiKeys: ApiKey[] }>());
export const getProgramApiKeysFailure = createAction("@program/get-program-api-keys-failure");

export const getApiKey = createAction(
  "@program/get-api-key",
  props<AdminControllerGetProgramApiKeyV1RequestParams & { callback?: (apiKey: ApiKey) => void }>(),
);
export const getApiKeySuccess = createAction("@program/get-api-key-success", props<{ apiKey: ApiKey; callback?: (apiKey: ApiKey) => void }>());
export const getApiKeyFailure = createAction("@program/get-api-key-failure", props<{ error: string }>());

export const generateApiKey = createAction(
  "@program/generate-api-key",
  props<AdminControllerGenerateApiKeyV1RequestParams & { callback: (apiKey: ApiKey) => void }>(),
);
export const generateApiKeySuccess = createAction(
  "@program/generate-api-key-success",
  props<{ apiKey: ApiKey; callback: (apiKey: ApiKey) => void }>(),
);
export const generateApiKeyFailure = createAction("@program/generate-api-key-failure", props<{ error: string }>());

export const updateApiKey = createAction(
  "@program/update-api-key",
  props<AdminControllerUpdateApiKeyV1RequestParams & { callback?: (apiKey: ApiKey) => void }>(),
);
export const updateApiKeySuccess = createAction("@program/update-api-key-success", props<{ apiKey: ApiKey; callback?: (apiKey: ApiKey) => void }>());
export const updateApiKeyFailure = createAction("@program/update-api-key-failure", props<{ error: string }>());

export const deleteApiKey = createAction("@program/delete-api-key", props<AdminControllerDeleteApiKeyV1RequestParams & { callback?: () => void }>());
export const deleteApiKeySuccess = createAction("@program/delete-api-key-success", props<{ apiKeyId: string; callback?: () => void }>());
export const deleteApiKeyFailure = createAction("@program/delete-api-key-failure", props<{ error: string }>());

export const getProgramIntegrations = createAction(
  "@program/get-program-integrations",
  props<ProgramControllerGetProgramIntegrationsV1RequestParams & { isLongPolling?: boolean; callback?: () => void }>(),
);
export const getProgramIntegrationsSuccess = createAction(
  "@program/get-program-integrations-success",
  props<{ programIntegrations: ProgramIntegration[]; callback?: () => void }>(),
);
export const getProgramIntegrationsFailure = createAction("@program/get-program-integrations-failure");

export const addIntegration = createAction("@program/add-integration", props<AdminControllerAddIntegrationV1RequestParams>());
export const addIntegrationSuccess = createAction("@program/add-integration-success");
export const addIntegrationFailure = createAction("@program/add-integration-failure");

export const updateProgramIntegration = createAction(
  "@program/update-program-integration",
  props<AdminControllerUpdateProgramIntegrationV1RequestParams & { integrationName: string; callback: () => void }>(),
);
export const updateProgramIntegrationSuccess = createAction(
  "@program/update-program-integration-success",
  props<{ programIntegration: ProgramIntegration; integrationName: string; callback: () => void }>(),
);
export const updateProgramIntegrationFailure = createAction("@program/update-program-integration-failure", props<{ formError: string }>());

export const addProgramIntegration = createAction(
  "@program/add-program-integration",
  props<AdminControllerAddIntegrationV1RequestParams & { integrationName: string; callback: () => void }>(),
);
export const addProgramIntegrationSuccess = createAction(
  "@program/add-program-integration-success",
  props<{ programIntegration: ProgramIntegration; integrationName: string; callback: () => void }>(),
);
export const addProgramIntegrationFailure = createAction("@program/add-program-integration-failure", props<{ formError: string }>());

export const getMcpSettings = createAction("@program/get-mcp-settings", props<{ programId: string }>());
export const getMcpSettingsSuccess = createAction("@program/get-mcp-settings-success", props<{ mcpSettings: McpSettings }>());
export const getMcpSettingsFailure = createAction("@program/get-mcp-settings-failure");

export const revokeMcpConnection = createAction(
  "@program/revoke-mcp-connection",
  props<{ programId: string; connectionId: string; callback?: () => void }>(),
);
export const revokeMcpConnectionSuccess = createAction(
  "@program/revoke-mcp-connection-success",
  props<{ connectionId: string; callback?: () => void }>(),
);
export const revokeMcpConnectionFailure = createAction("@program/revoke-mcp-connection-failure", props<{ formError: string }>());

export const deactivateMcp = createAction("@program/deactivate-mcp", props<{ programId: string; callback?: () => void }>());
export const deactivateMcpSuccess = createAction("@program/deactivate-mcp-success", props<{ callback?: () => void }>());
export const deactivateMcpFailure = createAction("@program/deactivate-mcp-failure", props<{ formError: string }>());

export const tokenExchange = createAction("@program/token-exchange", props<ShopifyControllerTokenExchangeV1RequestParams>());
export const tokenExchangeSuccess = createAction("@program/token-exchange-success");
export const tokenExchangeFailure = createAction("@program/token-exchange-failure");
export const setShopifyPlan = createAction("@program/set-shopify-plan", props<Pick<Program, "shopifyPlus" | "shopifyPlanName">>());

export const sendPush = createAction("@program/send-push", props<CustomerActionsControllerSendPushV1RequestParams & { callback: () => void }>());
export const sendPushSuccess = createAction("@program/send-push-success", props<{ callback: () => void }>());
export const sendPushFailure = createAction("@program/send-push-failure", props<{ formError: string }>());

export const klaviyoAuthorize = createAction("@program/klaviyo-authorize", props<KlaviyoControllerAuthorizeV1RequestParams>());
export const klaviyoAuthorizeSuccess = createAction("@program/klaviyo-authorize-success", props<{ redirectUri: string }>());
export const klaviyoAuthorizeFailure = createAction("@program/klaviyo-authorize-failure");

export const klaviyoCallback = createAction("@program/klaviyo-callback", props<KlaviyoControllerCallbackV1RequestParams>());
export const klaviyoCallbackSuccess = createAction("@program/klaviyo-callback-success", props<{ programId: string }>());
export const klaviyoCallbackFailure = createAction("@program/klaviyo-callback-failure");

export const klaviyoRevoke = createAction("@program/klaviyo-revoke", props<KlaviyoControllerRevokeV1RequestParams>());
export const klaviyoRevokeSuccess = createAction("@program/klaviyo-revoke-success", props<{ programId: string }>());
export const klaviyoRevokeFailure = createAction("@program/klaviyo-revoke-failure", props<{ reason: string }>());

export const setCustomersSearchTerm = createAction("@program/set-customers-search-term", props<{ customersSearchTerm: string }>());

export const setCustomersSort = createAction(
  "@program/set-customers-sort",
  props<{ sort: CustomersSortField; sortDirection: 'ASC' | 'DESC' }>(),
);

export const setCouponsSearchTerm = createAction("@program/set-coupons-search-term", props<{ couponsSearchTerm: string }>());

export const resetCustomersPagination = createAction("@program/reset-customers-pagination");

export const setIntegrationsBySearchTerm = createAction(
  "@program/set-integrations-by-search-term",
  props<{ integrationsSearchTerm: string }>(),
);

export const getFeatures = createAction("@program/get-features");
export const getFeaturesSuccess = createAction("@program/get-features-success", props<{ features: Feature[] }>());
export const getFeaturesFailure = createAction("@program/get-features-failure");

export const toggleCouponsFilter = createAction("@program/toggle-coupons-filter", props<{ couponsFilter: CouponDto.StatusEnum }>());
export const setRewardsView = createAction("@program/set-rewards-view", props<{ rewardsView: RewardsView }>());

export const getSegments = createAction("@program/get-segments", props<AdminControllerGetSegmentsV1RequestParams>());
export const getSegmentsSuccess = createAction("@program/get-segments-success", props<{ segments: SegmentDto[] }>());
export const getSegmentsFailure = createAction("@program/get-segments-failure");

export const addPointsToCustomer = createAction(
  "@program/add-points-to-customer",
  props<AdminControllerAddPointsToCustomerV1RequestParams & { callback: () => void }>(),
);
export const addPointsToCustomerSuccess = createAction(
  "@program/add-points-to-customer-success",
  props<{ customer: CustomerData; callback: () => void }>(),
);
export const addPointsToCustomerFailure = createAction("@program/add-points-to-customer-failure", props<{ formError: string }>());

export const removePointsToCustomer = createAction(
  "@program/remove-points-to-customer",
  props<AdminControllerRemovePointsToCustomerV1RequestParams & { callback: () => void }>(),
);
export const removePointsToCustomerSuccess = createAction(
  "@program/remove-points-to-customer-success",
  props<{ customer: CustomerData; callback: () => void }>(),
);
export const removePointsToCustomerFailure = createAction("@program/remove-points-to-customer-failure", props<{ formError: string }>());

export const getCustomerEvents = createAction("@program/get-customer-events", props<AdminControllerGetCustomerEventsV1RequestParams>());
export const getCustomerEventsSuccess = createAction(
  "@program/get-customer-events-success",
  props<{ customerId: string; paginatedCustomerEvents: PaginatedCustomerEvents }>(),
);
export const getCustomerEventsFailure = createAction("@program/get-customer-events-failure");

export const getProgramSubscriptionInfo = createAction(
  "@program/get-program-subscription-info",
  props<{ params: AdminControllerGetProgramSubscriptionInfoV1RequestParams; isLongPolling?: boolean }>(),
);
export const getProgramSubscriptionInfoSuccess = createAction(
  "@program/get-program-subscription-info-success",
  props<{ subscriptionInfo: SubscriptionInfo; programId: string; isLongPolling: boolean }>(),
);
export const getProgramSubscriptionInfoFailure = createAction("@program/get-program-subscription-info-failure");

export const setPaymentRequired = createAction("@program/set-payment-required", props<{ reason: SubscriptionPaymentRequiredReason }>());

export const createProgramSubscription = createAction(
  "@program/create-program-subscription",
  props<{ params: AdminControllerCreateProgramSubscriptionV1RequestParams; navigate?: boolean; callback?: () => void }>(),
);
export const createProgramSubscriptionSuccess = createAction(
  "@program/create-program-subscription-success",
  props<{ redirectLink: RedirectLink; navigate?: boolean; callback?: () => void }>(),
);
export const createProgramSubscriptionFailure = createAction("@program/create-program-subscription-failure", props<{ reason: string }>());

export const setLoadingCreateProgramSubscription = createAction(
  "@program/set-loading-create-program-subscription",
  props<{ loadingCreateSubscription: boolean }>(),
);

export const cancelProgramSubscription = createAction(
  "@program/cancel-program-subscription",
  props<{ params: AdminControllerCancelProgramSubscriptionV1RequestParams }>(),
);
export const cancelProgramSubscriptionSuccess = createAction(
  "@program/cancel-program-subscription-success",
  props<{ programId: string }>(),
);
export const cancelProgramSubscriptionFailure = createAction("@program/cancel-program-subscription-failure");

export const setLoadingCancelProgramSubscription = createAction(
  "@program/set-loading-cancel-program-subscription",
  props<{ loadingCancelSubscription: boolean }>(),
);

export const setLoadingKlaviyoAuth = createAction("@program/set-loading-klaviyo-auth", props<{ loadingKlaviyoAuth: boolean }>());

export const getCampaignById = createAction("@program/get-campaign-by-id", props<AdminControllerGetCampaignByIdV1RequestParams>());
export const getCampaignByIdSuccess = createAction("@program/get-campaign-by-id-success", props<{ campaign: CampaignDto }>());
export const getCampaignByIdFailure = createAction(
  "@program/get-campaign-by-id-failure",
  props<{ campaignId: string; status: number }>(),
);

export const checkCampaignSlug = createAction("@program/check-campaign-slug", props<AdminControllerCheckCampaignSlugV1RequestParams>());
export const checkCampaignSlugSuccess = createAction("@program/check-campaign-slug-success", props<{ response: SlugDto }>());
export const checkCampaignSlugFailure = createAction("@program/check-campaign-slug-failure");

export const resetCampaignSlug = createAction("@program/reset-campaign-slug");

export const getCoupons = createAction("@program/get-coupons", props<CouponControllerGetCouponsV1RequestParams>());
export const getCouponsSuccess = createAction("@program/get-coupons-success", props<{ limit: number; coupons: PaginatedCoupons }>());
export const getCouponsFailure = createAction("@program/get-coupons-failure");

export const getCouponById = createAction(
  "@program/get-coupon-by-id",
  props<CouponControllerGetCouponByIdV1RequestParams & { isLongPolling?: boolean }>(),
);
export const getCouponByIdSuccess = createAction("@program/get-coupon-by-id-success", props<{ coupon: CouponInfoDto }>());
export const getCouponByIdFailure = createAction("@program/get-coupon-by-id-failure");

export const generateCouponCodeBatch = createAction(
  "@program/generate-coupon-code-batch",
  props<CouponControllerCreateCouponBatchV1RequestParams & { callback: () => void }>(),
);
export const generateCouponCodeBatchSuccess = createAction(
  "@program/generate-coupon-code-batch-success",
  props<{
    couponId: string;
    createdFrom: string;
    codesRequested: number;
    // Carried into the placeholder row so it renders as the batch that was created
    // (on demand, variable amount...) before the next refresh brings the real one.
    generationMode?: CouponBatchDto.GenerationModeEnum;
    discountType?: CouponBatchDto.DiscountTypeEnum;
    value?: number | null;
    expiresInDays?: number | null;
    callback: () => void;
  }>(),
);
export const generateCouponCodeBatchFailure = createAction("@program/generate-coupon-code-batch-failure", props<{ formError: string }>());

export const removeUnclaimed = createAction("@program/remove-unclaimed", props<CouponControllerRemoveUnclaimedCodesV1RequestParams>());
export const removeUnclaimedSuccess = createAction("@program/remove-unclaimed-success", props<{ coupon: CouponInfoDto }>());
export const removeUnclaimedFailure = createAction("@program/remove-unclaimed-failure", props<{ formError: string }>());

export const removeCouponBatch = createAction("@program/remove-coupon-batch", props<CouponControllerRemoveCouponBatchV1RequestParams>());
export const removeCouponBatchSuccess = createAction("@program/remove-coupon-batch-success", props<{ coupon: CouponInfoDto }>());
export const removeCouponBatchFailure = createAction("@program/remove-coupon-batch-failure", props<{ formError: string }>());

export const searchCoupons = createAction("@program/search-coupons", props<CouponControllerSearchCouponsV1RequestParams>());
export const searchCouponsSuccess = createAction("@program/search-coupons-success", props<{ results: MinimalCoupon[] }>());
export const searchCouponsFailure = createAction("@program/search-coupons-failure", props<{ reason: string }>());

export const getCouponStats = createAction("@program/get-coupon-stats", props<CouponControllerGetCouponCountsV1RequestParams>());
export const getCouponStatsSuccess = createAction("@program/get-coupon-stats-success", props<{ couponId: string; stats: CouponCounts }>());
export const getCouponStatsFailure = createAction("@program/get-coupon-stats-failure", props<{ reason: string }>());

export const claimAndDownloadCouponCodes = createAction(
  "@program/claim-and-download-coupon-codes",
  props<CouponControllerClaimAndDownloadCodesV1RequestParams & { callback: () => void }>(),
);
export const claimAndDownloadCouponCodesSuccess = createAction(
  "@program/claim-and-download-coupon-codes-success",
  props<{ csv: string; callback: () => void }>(),
);
export const claimAndDownloadCouponCodesFailure = createAction(
  "@program/claim-and-download-coupon-codes-failure",
  props<{ reason: string }>(),
);

export const getProgramRewards = createAction("@program/get-program-rewards", props<CouponControllerGetRewardsV1RequestParams>());
export const getProgramRewardsSuccess = createAction(
  "@program/get-program-rewards-success",
  props<{ rewards: RewardWithCouponCodeCountsDto[]; details: PublicRewardDto[] }>(),
);
export const getProgramRewardsFailure = createAction("@program/get-program-rewards-failure");

export const reorderProgramRewards = createAction(
  "@program/reorder-program-rewards",
  props<{ previousIndex: number; currentIndex: number }>(),
);
export const reorderProgramRewardsSuccess = createAction("@program/reorder-program-rewards-success");
export const reorderProgramRewardsFailure = createAction("@program/reorder-program-rewards-failure", props<{ reason: string }>());

export const getRewardById = createAction("@program/get-reward-by-id", props<CouponControllerGetRewardV1RequestParams>());
export const getRewardByIdSuccess = createAction("@program/get-reward-by-id-success", props<{ reward: RewardDto }>());
export const getRewardByIdFailure = createAction("@program/get-reward-by-id-failure");

export const createReward = createAction(
  "@program/create-reward",
  props<Omit<CouponControllerCreateRewardV1RequestParams, "couponId"> & { pendingCouponBatch?: CreateCouponBatchDto; callback?: () => void }>(),
);
export const createRewardSuccess = createAction("@program/create-reward-success", props<{ reward: RewardDto; callback?: () => void }>());
export const createRewardFailure = createAction("@program/create-reward-failure", props<{ formError: string }>());

export const updateReward = createAction("@program/update-reward", props<CouponControllerUpdateRewardV1RequestParams & { callback?: () => void }>());
export const updateRewardSuccess = createAction("@program/update-reward-success", props<{ reward: RewardDto; callback?: () => void }>());
export const updateRewardFailure = createAction("@program/update-reward-failure", props<{ formError: string }>());

export const deleteReward = createAction("@program/delete-reward", props<CouponControllerDeleteRewardV1RequestParams>());
export const deleteRewardSuccess = createAction("@program/delete-reward-success", props<{ rewardId: string }>());
export const deleteRewardFailure = createAction("@program/delete-reward-failure", props<{ formError: string }>());

export const sendGiftCard = createAction("@program/send-gift-card", props<{ email: string; campaignSlug: string }>());
export const sendGiftCardSuccess = createAction("@program/send-gift-card-success");
export const sendGiftCardFailure = createAction("@program/send-gift-card-failure", props<{ error: string }>());

// Without a toastSelector the toast shows over the viewport
export const sendLoyaltyCard = createAction("@program/send-loyalty-card", props<{ email: string; toastSelector?: string }>());
export const sendLoyaltyCardSuccess = createAction("@program/send-loyalty-card-success", props<{ toastSelector?: string }>());
export const sendLoyaltyCardFailure = createAction(
  "@program/send-loyalty-card-failure",
  props<{ error: string; toastSelector?: string }>(),
);

export const assignCoupon = createAction("@program/assign-coupon", props<{ email: string; campaignSlug: string }>());
export const assignCouponSuccess = createAction("@program/assign-coupon-success");
export const assignCouponFailure = createAction("@program/assign-coupon-failure", props<{ error: string }>());

export const getAllowedDomains = createAction("@program/get-allowed-domains");
export const getAllowedDomainsSuccess = createAction("@program/get-allowed-domains-success", props<{ allowedDomains: AllowedDomain[] }>());
export const getAllowedDomainsFailure = createAction("@program/get-allowed-domains-failure");

export const validateDomain = createAction("@program/validate-domain", props<{ programId: string; domain: string }>());
export const validateDomainSuccess = createAction("@program/validate-domain-success", props<{ availableDomain: boolean }>());
export const validateDomainFailure = createAction("@program/validate-domain-failure", props<{ error: string }>());
export const resetAvailableDomain = createAction("@program/reset-available-domain");

export const addDomain = createAction("@program/save-domain", props<{ domain: string; callback: () => void}>());
export const addDomainSuccess = createAction("@program/save-domain-success", props<{ domain: AllowedDomain; callback: () => void }>());
export const addDomainFailure = createAction("@program/save-domain-failure", props<{ error: string }>());

export const editDomain = createAction("@program/edit-allowed-domain", props<{ domain: string; id: string; callback: () => void }>());
export const editDomainSuccess = createAction("@program/edit-allowed-domain-success",
  props<{ domain: AllowedDomain; callback: () => void }>(),
);
export const editDomainFailure = createAction("@program/edit-allowed-domain-failure", props<{ error: string }>());

export const deleteDomain = createAction("@program/delete-allowed-domain", props<{ id: string; callback?: () => void }>());
export const deleteDomainSuccess = createAction("@program/delete-allowed-domain-success", props<{ id: string; callback?: () => void }>());
export const deleteDomainFailure = createAction("@program/delete-allowed-domain-failure", props<{ error: string }>());

export const updateRegisterFields = createAction("@program/update-register-fields", props<{ registerFields: RegisterField[] }>());

export const saveFromOnboarding = createAction(
  "@program/save-from-onboarding",
  props<{
    rewards: (Partial<RewardDto> & { value: number; minimumSpend: number; nameKey: OnboardingRewardNameKey })[];
    domainSlug: string;
    creditRate?: number;
    sector: string;
    interests: string[];
    callback: () => void;
  }>(),
);
export const saveFromOnboardingSuccess = createAction(
  "@program/save-from-onboarding-success",
  props<{
    callback: () => void;
    rewards: RewardDto[];
    coupons: CouponDto[];
    campaigns: CampaignDto[];
    updatedProgram?: Program;
    allowedDomain?: AllowedDomain;
    programEngagementFlow?: ProgramEngagementFlow;
  }>(),
);
export const saveFromOnboardingFailure = createAction(
  "@program/save-from-onboarding-failure",
  props<{ callback: () => void; error: string }>(),
);

export const trackOnboardingStep = createAction(
  "@program/track-onboarding-step",
  props<{ step: string; event: "viewed" | "completed"; properties?: Record<string, unknown> }>(),
);


export const getEngagements = createAction("@program/get-engagements");
export const getEngagementsSuccess =
createAction("@program/get-engagements-success", props<{ engagements: EngagementFlowWithProgramEngagementFlowDto[] }>());
export const getEngagementsFailure = createAction("@program/get-engagements-failure", props<{ error: string }>());

export const createEngagement = createAction(
  "@program/create-engagement",
  props<Omit<AdminControllerCreateProgramEngagementFlowV1RequestParams, "programId"> & { callback?: () => void }>(),
);
export const createEngagementSuccess = createAction(
  "@program/create-engagement-success",
  props<{ programEngagementFlow: ProgramEngagementFlow; callback?: () => void }>(),
);
// profileFormError: what a rejected custom profile form points at (the page marks that field)
export const createEngagementFailure = createAction(
  "@program/create-engagement-failure",
  props<{ error: string; profileFormError?: ProfileFormSaveError }>(),
);

export const updateEngagement = createAction(
  "@program/update-engagement",
  props<Omit<AdminControllerUpdateProgramEngagementFlowV1RequestParams, "programId"> & { callback?: () => void }>(),
);
export const updateEngagementSuccess = createAction(
  "@program/update-engagement-success",
  props<{ programEngagementFlow: ProgramEngagementFlow; callback?: () => void }>(),
);
export const updateEngagementFailure = createAction(
  "@program/update-engagement-failure",
  props<{ error: string; profileFormError?: ProfileFormSaveError }>(),
);

// Shopify customer metafield definitions a profile form field can write to
export const getCustomerMetafieldDefinitions = createAction(
  "@program/get-customer-metafield-definitions",
  props<{ programId: string }>(),
);
export const getCustomerMetafieldDefinitionsSuccess = createAction(
  "@program/get-customer-metafield-definitions-success",
  props<{ definitions: CustomerMetafieldDefinitionDto[] }>(),
);
export const getCustomerMetafieldDefinitionsFailure = createAction(
  "@program/get-customer-metafield-definitions-failure",
  props<{ error: string }>(),
);

export const createCustomerMetafieldDefinition = createAction(
  "@program/create-customer-metafield-definition",
  props<{ programId: string; definition: CreateCustomerMetafieldDefinitionDto; callback?: () => void }>(),
);
export const createCustomerMetafieldDefinitionSuccess = createAction(
  "@program/create-customer-metafield-definition-success",
  props<{ definition: CustomerMetafieldDefinitionDto; callback?: () => void }>(),
);
export const createCustomerMetafieldDefinitionFailure = createAction(
  "@program/create-customer-metafield-definition-failure",
  props<{ error: string }>(),
);

export const deleteEngagement = createAction(
  "@program/delete-engagement",
  // engagementFlowId is included alongside the request params so the reducer
  // can find the engagement row (keyed by engagement flow id) and clear its
  // programEngagementFlows without re-fetching.
  props<Omit<AdminControllerDeleteProgramEngagementFlowV1RequestParams, "programId"> & { engagementFlowId: string; callback?: () => void }>(),
);
export const deleteEngagementSuccess = createAction(
  "@program/delete-engagement-success",
  props<{ engagementFlowId: string; callback?: () => void }>(),
);
export const deleteEngagementFailure = createAction("@program/delete-engagement-failure", props<{ error: string }>());

// Program languages
export const getProgramLanguages = createAction("@program/get-program-languages", props<{ programId: string }>());
export const getProgramLanguagesSuccess = createAction(
  "@program/get-program-languages-success",
  props<{ programLanguages: ProgramLanguagesResponseDto }>(),
);
export const getProgramLanguagesFailure = createAction("@program/get-program-languages-failure", props<{ error: string }>());

export const setProgramLanguages = createAction(
  "@program/set-program-languages",
  props<{ programId: string; setProgramLanguagesDto: SetProgramLanguagesDto; toastId?: string; callback?: () => void }>(),
);
export const setProgramLanguagesSuccess = createAction(
  "@program/set-program-languages-success",
  props<{ programLanguages: ProgramLanguagesResponseDto; toastId?: string; callback?: () => void }>(),
);
export const setProgramLanguagesFailure = createAction("@program/set-program-languages-failure", props<{ error: string }>());

export const getTemplateMetadata = createAction("@program/get-template-metadata", props<{ programId: string }>());
export const getTemplateMetadataSuccess = createAction(
  "@program/get-template-metadata-success",
  props<{ templateMetadata: TemplateMetadata }>(),
);
export const getTemplateMetadataFailure = createAction("@program/get-template-metadata-failure", props<{ error: string }>());

// Section translations
// `entityType` defaults to "section"; the design editor also loads its front texts ("wallet_field") through these
export const getSectionTranslations = createAction(
  "@program/get-section-translations",
  props<{ programId: string; locale: SetProgramLanguagesDto.LocalesEnum; entityType?: string }>(),
);
export const getSectionTranslationsSuccess = createAction(
  "@program/get-section-translations-success",
  props<{ locale: string; translations: EntityTranslation[]; entityType?: string }>(),
);
export const getSectionTranslationsFailure = createAction("@program/get-section-translations-failure", props<{ error: string }>());

export const upsertSectionTranslations = createAction(
  "@program/upsert-section-translations",
  props<{ programId: string; upsertTranslationsBulkDto: UpsertTranslationsBulkDto }>(),
);
export const upsertSectionTranslationsSuccess = createAction(
  "@program/upsert-section-translations-success",
  props<{ translations: UpsertTranslationDto[] }>(),
);
export const upsertSectionTranslationsFailure = createAction("@program/upsert-section-translations-failure", props<{ error: string }>());

export const autoTranslateSections = createAction(
  "@program/auto-translate-sections",
  props<{ programId: string; autoTranslateDto: AutoTranslateDto; callback: () => void }>(),
);
export const autoTranslateSectionsSuccess = createAction("@program/auto-translate-sections-success", props<{ callback: () => void }>());
export const autoTranslateSectionsFailure = createAction("@program/auto-translate-sections-failure", props<{ error: string }>());

// Earning flow translations
export const getEarningFlowTranslations = createAction(
  "@program/get-earning-flow-translations",
  props<{ programId: string; locale: SetProgramLanguagesDto.LocalesEnum }>(),
);
export const getEarningFlowTranslationsSuccess = createAction(
  "@program/get-earning-flow-translations-success",
  props<{ locale: string; translations: EntityTranslation[] }>(),
);
export const getEarningFlowTranslationsFailure = createAction(
  "@program/get-earning-flow-translations-failure",
  props<{ error: string }>(),
);

export const upsertEarningFlowTranslations = createAction(
  "@program/upsert-earning-flow-translations",
  props<{ programId: string; upsertTranslationsBulkDto: UpsertTranslationsBulkDto }>(),
);
export const upsertEarningFlowTranslationsSuccess = createAction(
  "@program/upsert-earning-flow-translations-success",
  props<{ translations: UpsertTranslationDto[] }>(),
);
export const upsertEarningFlowTranslationsFailure = createAction(
  "@program/upsert-earning-flow-translations-failure",
  props<{ error: string }>(),
);

export const autoTranslateEarningFlows = createAction(
  "@program/auto-translate-earning-flows",
  props<{ programId: string; autoTranslateDto: AutoTranslateDto; callback: () => void }>(),
);
export const autoTranslateEarningFlowsSuccess = createAction(
  "@program/auto-translate-earning-flows-success",
  props<{ callback: () => void }>(),
);
export const autoTranslateEarningFlowsFailure = createAction(
  "@program/auto-translate-earning-flows-failure",
  props<{ error: string }>(),
);

// Profile form translations (sections, fields and options of a custom COMPLETE_USER_PROFILE form)
export const getProfileFormTranslations = createAction(
  "@program/get-profile-form-translations",
  props<{ programId: string; locale: SetProgramLanguagesDto.LocalesEnum }>(),
);
export const getProfileFormTranslationsSuccess = createAction(
  "@program/get-profile-form-translations-success",
  props<{ locale: string; translations: EntityTranslation[] }>(),
);
export const getProfileFormTranslationsFailure = createAction(
  "@program/get-profile-form-translations-failure",
  props<{ error: string }>(),
);

export const upsertProfileFormTranslations = createAction(
  "@program/upsert-profile-form-translations",
  props<{ programId: string; upsertTranslationsBulkDto: UpsertTranslationsBulkDto; callback?: () => void }>(),
);
export const upsertProfileFormTranslationsSuccess = createAction(
  "@program/upsert-profile-form-translations-success",
  props<{ translations: UpsertTranslationDto[]; callback?: () => void }>(),
);
export const upsertProfileFormTranslationsFailure = createAction(
  "@program/upsert-profile-form-translations-failure",
  props<{ error: string }>(),
);

export const autoTranslateProfileForm = createAction(
  "@program/auto-translate-profile-form",
  props<{ programId: string; autoTranslateDto: AutoTranslateDto; callback: () => void }>(),
);
export const autoTranslateProfileFormSuccess = createAction(
  "@program/auto-translate-profile-form-success",
  props<{ callback: () => void }>(),
);
export const autoTranslateProfileFormFailure = createAction(
  "@program/auto-translate-profile-form-failure",
  props<{ error: string }>(),
);

// Reward translations
export const getRewardTranslations = createAction(
  "@program/get-reward-translations",
  props<{ programId: string; locale: SetProgramLanguagesDto.LocalesEnum }>(),
);
export const getRewardTranslationsSuccess = createAction(
  "@program/get-reward-translations-success",
  props<{ locale: string; translations: EntityTranslation[] }>(),
);
export const getRewardTranslationsFailure = createAction(
  "@program/get-reward-translations-failure",
  props<{ error: string }>(),
);

export const upsertRewardTranslations = createAction(
  "@program/upsert-reward-translations",
  props<{ programId: string; upsertTranslationsBulkDto: UpsertTranslationsBulkDto }>(),
);
export const upsertRewardTranslationsSuccess = createAction(
  "@program/upsert-reward-translations-success",
  props<{ translations: UpsertTranslationDto[] }>(),
);
export const upsertRewardTranslationsFailure = createAction(
  "@program/upsert-reward-translations-failure",
  props<{ error: string }>(),
);

export const autoTranslateRewards = createAction(
  "@program/auto-translate-rewards",
  props<{ programId: string; autoTranslateDto: AutoTranslateDto; callback: () => void }>(),
);
export const autoTranslateRewardsSuccess = createAction(
  "@program/auto-translate-rewards-success",
  props<{ callback: () => void }>(),
);
export const autoTranslateRewardsFailure = createAction(
  "@program/auto-translate-rewards-failure",
  props<{ error: string }>(),
);

// Tier translations
export const getTierTranslations = createAction(
  "@program/get-tier-translations",
  props<{ programId: string; locale: SetProgramLanguagesDto.LocalesEnum }>(),
);
export const getTierTranslationsSuccess = createAction(
  "@program/get-tier-translations-success",
  props<{ locale: string; translations: EntityTranslation[] }>(),
);
export const getTierTranslationsFailure = createAction(
  "@program/get-tier-translations-failure",
  props<{ error: string }>(),
);

export const upsertTierTranslations = createAction(
  "@program/upsert-tier-translations",
  props<{ programId: string; upsertTranslationsBulkDto: UpsertTranslationsBulkDto }>(),
);
export const upsertTierTranslationsSuccess = createAction(
  "@program/upsert-tier-translations-success",
  props<{ translations: UpsertTranslationDto[] }>(),
);
export const upsertTierTranslationsFailure = createAction(
  "@program/upsert-tier-translations-failure",
  props<{ error: string }>(),
);

export const autoTranslateTiers = createAction(
  "@program/auto-translate-tiers",
  props<{ programId: string; autoTranslateDto: AutoTranslateDto; callback: () => void }>(),
);
export const autoTranslateTiersSuccess = createAction(
  "@program/auto-translate-tiers-success",
  props<{ callback: () => void }>(),
);
export const autoTranslateTiersFailure = createAction(
  "@program/auto-translate-tiers-failure",
  props<{ error: string }>(),
);

// Coupon translations
export const getCouponTranslations = createAction(
  "@program/get-coupon-translations",
  props<{ programId: string; locale: SetProgramLanguagesDto.LocalesEnum }>(),
);
export const getCouponTranslationsSuccess = createAction(
  "@program/get-coupon-translations-success",
  props<{ locale: string; translations: EntityTranslation[] }>(),
);
export const getCouponTranslationsFailure = createAction(
  "@program/get-coupon-translations-failure",
  props<{ error: string }>(),
);

export const upsertCouponTranslations = createAction(
  "@program/upsert-coupon-translations",
  props<{ programId: string; upsertTranslationsBulkDto: UpsertTranslationsBulkDto }>(),
);
export const upsertCouponTranslationsSuccess = createAction(
  "@program/upsert-coupon-translations-success",
  props<{ translations: UpsertTranslationDto[] }>(),
);
export const upsertCouponTranslationsFailure = createAction(
  "@program/upsert-coupon-translations-failure",
  props<{ error: string }>(),
);

export const autoTranslateCoupons = createAction(
  "@program/auto-translate-coupons",
  props<{ programId: string; autoTranslateDto: AutoTranslateDto; callback: () => void }>(),
);
export const autoTranslateCouponsSuccess = createAction(
  "@program/auto-translate-coupons-success",
  props<{ callback: () => void }>(),
);
export const autoTranslateCouponsFailure = createAction(
  "@program/auto-translate-coupons-failure",
  props<{ error: string }>(),
);

// Campaign translations
export const getCampaignTranslations = createAction(
  "@program/get-campaign-translations",
  props<{ programId: string; locale: SetProgramLanguagesDto.LocalesEnum }>(),
);
export const getCampaignTranslationsSuccess = createAction(
  "@program/get-campaign-translations-success",
  props<{ locale: string; translations: EntityTranslation[] }>(),
);
export const getCampaignTranslationsFailure = createAction(
  "@program/get-campaign-translations-failure",
  props<{ error: string }>(),
);

export const upsertCampaignTranslations = createAction(
  "@program/upsert-campaign-translations",
  props<{ programId: string; upsertTranslationsBulkDto: UpsertTranslationsBulkDto }>(),
);
export const upsertCampaignTranslationsSuccess = createAction(
  "@program/upsert-campaign-translations-success",
  props<{ translations: UpsertTranslationDto[] }>(),
);
export const upsertCampaignTranslationsFailure = createAction(
  "@program/upsert-campaign-translations-failure",
  props<{ error: string }>(),
);

export const autoTranslateCampaigns = createAction(
  "@program/auto-translate-campaigns",
  props<{ programId: string; autoTranslateDto: AutoTranslateDto; callback: () => void }>(),
);
export const autoTranslateCampaignsSuccess = createAction(
  "@program/auto-translate-campaigns-success",
  props<{ callback: () => void }>(),
);
export const autoTranslateCampaignsFailure = createAction(
  "@program/auto-translate-campaigns-failure",
  props<{ error: string }>(),
);

// Program-level translations (notification messages, etc.)
export const getProgramTranslations = createAction(
  "@program/get-program-translations",
  props<{ programId: string; locale: SetProgramLanguagesDto.LocalesEnum }>(),
);
export const getProgramTranslationsSuccess = createAction(
  "@program/get-program-translations-success",
  props<{ locale: string; translations: EntityTranslation[] }>(),
);
export const getProgramTranslationsFailure = createAction(
  "@program/get-program-translations-failure",
  props<{ error: string }>(),
);

export const upsertProgramTranslations = createAction(
  "@program/upsert-program-translations",
  props<{ programId: string; upsertTranslationsBulkDto: UpsertTranslationsBulkDto }>(),
);
export const upsertProgramTranslationsSuccess = createAction(
  "@program/upsert-program-translations-success",
  props<{ translations: UpsertTranslationDto[] }>(),
);
export const upsertProgramTranslationsFailure = createAction(
  "@program/upsert-program-translations-failure",
  props<{ error: string }>(),
);

export const autoTranslateProgram = createAction(
  "@program/auto-translate-program",
  props<{ programId: string; autoTranslateDto: AutoTranslateDto; callback: () => void }>(),
);
export const autoTranslateProgramSuccess = createAction(
  "@program/auto-translate-program-success",
  props<{ callback: () => void }>(),
);
export const autoTranslateProgramFailure = createAction(
  "@program/auto-translate-program-failure",
  props<{ error: string }>(),
);
